<template>
  <div class="biometric-widget" :style="cssVars">
    <!-- ─── CAMERA VIEW (default) ─── -->
    <div
      v-if="
        ![
          'processing',
          'success',
          'failure',
          'no-camera',
          'insecure-context',
        ].includes(state)
      "
      ref="cameraScreen"
      class="camera-screen"
    >
      <!-- Live feed -->
      <video
        ref="videoElement"
        autoplay
        playsinline
        muted
        class="camera-feed"
      ></video>

      <!-- Dark gradient top bar -->
      <div class="top-bar">
        <div v-if="config.logo" class="brand-logo">
          <img :src="config.logo" alt="Logo" />
        </div>

        <div
          v-if="debug && livenessMode !== 'loading'"
          class="liveness-mode-badge"
        >
          {{ livenessMode === "active" ? "🟢 Active Mode" : "⚪ Passive Mode" }}
          <template v-if="pose">
            · yaw {{ (pose.yaw - baselineYaw).toFixed(2) }} · pitch
            {{ (pose.pitch - baselinePitch).toFixed(2) }}
          </template>
        </div>

        <!-- Login / Register tabs -->
        <div class="segmented-control">
          <button
            :class="{ active: internalMode === 'login' }"
            @click="switchMode('login')"
          >
            Login
          </button>
          <button
            :class="{ active: internalMode === 'register' }"
            @click="switchMode('register')"
          >
            Register
          </button>
        </div>
      </div>

      <!-- Permission-request overlay -->
      <div v-if="state === 'permission-request'" class="overlay-message">
        <div class="perm-icon">📷</div>
        <p class="perm-title">Allow camera access</p>
        <p class="perm-sub">
          Tap below so we can use your
          {{
            internalMode === "login"
              ? "face to log you in"
              : "face to register you"
          }}.
        </p>
        <button
          class="btn-allow"
          :disabled="requestingPermission"
          @click="requestCameraPermission"
        >
          {{ requestingPermission ? "Requesting…" : "Allow camera" }}
        </button>
        <a href="#" class="fallback-link" @click.prevent="handleFallback">
          Use {{ internalMode === "login" ? "password" : "email" }} instead
        </a>
      </div>

      <!-- Permission-denied overlay -->
      <div v-if="state === 'permission-denied'" class="overlay-message">
        <div class="perm-icon">🚫</div>
        <p class="perm-title">Camera blocked</p>
        <div class="perm-steps">
          <p>{{ platformInstructions.step1 }}</p>
          <p>{{ platformInstructions.step2 }}</p>
          <p>{{ platformInstructions.step3 }}</p>
        </div>
        <button class="btn-allow" @click="requestCameraPermission">
          Try again
        </button>
        <a href="#" class="fallback-link" @click.prevent="handleFallback"
          >Use alternative method</a
        >
      </div>

      <!-- Camera active UI -->
      <template v-if="['camera-preview', 'capturing'].includes(state)">
        <!-- CLEAN VIEW - NO CLUTTER around the oval! -->
        <!-- Everything moved to bottom bar only -->

        <!-- Face oval guide -->
        <div ref="faceWrap" class="face-guide-wrap">
          <svg viewBox="0 0 200 260" class="face-guide-svg">
            <!-- Grey overlay mask REMOVED - just show oval only -->
            <ellipse
              cx="100"
              cy="130"
              rx="72"
              ry="96"
              fill="none"
              :stroke="ovalStrokeColor"
              stroke-width="3"
            />

            <!-- Active liveness: pulsing edge highlight arcs -->
            <g
              v-if="livenessMode === 'active' && edgeHighlightDirection"
              class="edge-highlights"
            >
              <template v-if="edgeHighlightDirection === 'left'">
                <path
                  d="M 28 130 A 72 96 0 0 1 54 44"
                  fill="none"
                  stroke="#2563EB"
                  stroke-width="8"
                  stroke-linecap="round"
                  class="pulse-arc"
                />
                <path
                  d="M 28 130 A 72 96 0 0 0 54 216"
                  fill="none"
                  stroke="#2563EB"
                  stroke-width="8"
                  stroke-linecap="round"
                  class="pulse-arc"
                />
              </template>
              <template v-if="edgeHighlightDirection === 'right'">
                <path
                  d="M 172 130 A 72 96 0 0 0 146 44"
                  fill="none"
                  stroke="#2563EB"
                  stroke-width="8"
                  stroke-linecap="round"
                  class="pulse-arc"
                />
                <path
                  d="M 172 130 A 72 96 0 0 1 146 216"
                  fill="none"
                  stroke="#2563EB"
                  stroke-width="8"
                  stroke-linecap="round"
                  class="pulse-arc"
                />
              </template>
              <path
                v-if="edgeHighlightDirection === 'up'"
                d="M 40 58 A 72 96 0 0 1 160 58"
                fill="none"
                stroke="#B91C1C"
                stroke-width="8"
                stroke-linecap="round"
                class="pulse-arc"
              />
              <path
                v-if="edgeHighlightDirection === 'down'"
                d="M 40 202 A 72 96 0 0 0 160 202"
                fill="none"
                stroke="#B91C1C"
                stroke-width="8"
                stroke-linecap="round"
                class="pulse-arc"
              />
            </g>

            <!-- Completed challenges: show as solid blue arcs (no pulse) -->
            <g v-if="livenessMode === 'active'" class="completed-highlights">
              <template
                v-for="challenge in currentChallenges"
                :key="challenge.key"
              >
                <template
                  v-if="challenge.completed && challenge.direction === 'left'"
                >
                  <path
                    d="M 28 130 A 72 96 0 0 1 54 44"
                    fill="none"
                    stroke="#2563EB"
                    stroke-width="6"
                    stroke-linecap="round"
                    opacity="0.9"
                  />
                  <path
                    d="M 28 130 A 72 96 0 0 0 54 216"
                    fill="none"
                    stroke="#2563EB"
                    stroke-width="6"
                    stroke-linecap="round"
                    opacity="0.9"
                  />
                </template>
                <template
                  v-if="challenge.completed && challenge.direction === 'right'"
                >
                  <path
                    d="M 172 130 A 72 96 0 0 0 146 44"
                    fill="none"
                    stroke="#2563EB"
                    stroke-width="6"
                    stroke-linecap="round"
                    opacity="0.9"
                  />
                  <path
                    d="M 172 130 A 72 96 0 0 1 146 216"
                    fill="none"
                    stroke="#2563EB"
                    stroke-width="6"
                    stroke-linecap="round"
                    opacity="0.9"
                  />
                </template>
                <path
                  v-if="challenge.completed && challenge.direction === 'up'"
                  d="M 40 58 A 72 96 0 0 1 160 58"
                  fill="none"
                  stroke="#2563EB"
                  stroke-width="6"
                  stroke-linecap="round"
                  opacity="0.9"
                />
                <path
                  v-if="challenge.completed && challenge.direction === 'down'"
                  d="M 40 202 A 72 96 0 0 0 160 202"
                  fill="none"
                  stroke="#2563EB"
                  stroke-width="6"
                  stroke-linecap="round"
                  opacity="0.9"
                />
              </template>
            </g>

            <!-- Facial feature guides REMOVED - keeps face area clean -->
            <!-- Eyes and nose markers removed to avoid clutter -->
          </svg>
        </div>

        <!-- Detection badge REMOVED - keeps oval area clean -->

        <!-- Prompt removed - everything shows at bottom only -->

        <!-- Bottom controls -->
        <div class="bottom-bar">
          <div class="mode-hint-container">
            <p class="mode-hint">{{ bottomHint }}</p>
            <!-- Progress bar during recording -->
            <div v-if="state === 'capturing'" class="recording-progress-bar">
              <div
                class="recording-progress-fill"
                :style="{ width: recordingProgressPercent + '%' }"
              ></div>
            </div>
          </div>

          <button
            v-if="showCaptureButton"
            class="btn-capture"
            :disabled="!canStartCapture"
            @click="startVideoCapture"
          >
            <span class="shutter-ring record"
              ><span class="shutter-dot"></span
            ></span>
            <span class="btn-label">{{ captureButtonLabel }}</span>
          </button>

          <!-- Recording pill removed - countdown shows in bottom text -->

          <a
            href="#"
            class="fallback-link-bottom"
            @click.prevent="handleFallback"
          >
            Use {{ internalMode === "login" ? "password" : "email" }} instead
          </a>
        </div>

        <button class="btn-x" @click="cancelCapture" aria-label="Cancel">
          ✕
        </button>
      </template>
    </div>

    <!-- ─── PROCESSING ─── -->
    <div v-if="state === 'processing'" class="status-screen">
      <div class="spinner"></div>
      <h2>
        {{
          internalMode === "login" ? "Verifying identity…" : "Registering face…"
        }}
      </h2>
    </div>

    <!-- ─── SUCCESS ─── -->
    <div v-if="state === 'success'" class="status-screen success-screen">
      <div class="status-icon success-icon">✓</div>
      <h2>
        {{
          internalMode === "login"
            ? "Login successful"
            : "Registration complete"
        }}
      </h2>
      <p>{{ successMessage }}</p>
    </div>

    <!-- ─── FAILURE ─── -->
    <div v-if="state === 'failure'" class="status-screen error-screen">
      <div class="status-icon error-icon">✗</div>
      <h2>
        {{ internalMode === "login" ? "Login failed" : "Registration failed" }}
      </h2>
      <p>{{ errorMessage }}</p>
      <button class="btn-primary" @click="retry">Try again</button>
      <a href="#" class="fallback-link" @click.prevent="handleFallback"
        >Use alternative method</a
      >
    </div>

    <!-- ─── NO CAMERA ─── -->
    <div v-if="state === 'no-camera'" class="status-screen error-screen">
      <div class="status-icon error-icon">📵</div>
      <h2>No camera detected</h2>
      <p>
        Your device doesn't have a camera or it isn't accessible from this
        browser.
      </p>
      <a href="#" class="fallback-link" @click.prevent="handleFallback"
        >Use alternative method</a
      >
    </div>

    <!-- ─── INSECURE CONTEXT ─── -->
    <div v-if="state === 'insecure-context'" class="status-screen error-screen">
      <div class="status-icon error-icon">🔓</div>
      <h2>Secure connection required</h2>
      <p>
        Camera access only works over HTTPS. Ask the host site to enable SSL.
      </p>
      <a href="#" class="fallback-link" @click.prevent="handleFallback"
        >Use alternative method</a
      >
    </div>

    <!-- ─── NEW: Accessibility Live Region (Screen Reader) ─── -->
    <div
      class="sr-only sr-announcer"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ accessibilityAnnouncement }}
    </div>

    <!-- ─── NEW: Audio Control Toggle ─── -->
    <button
      v-if="state === 'camera-preview'"
      class="audio-toggle"
      @click="toggleAudio"
      :aria-label="
        audioEnabled ? 'Disable audio feedback' : 'Enable audio feedback'
      "
      title="Toggle audio feedback"
    >
      {{ audioEnabled ? "🔊" : "🔇" }}
    </button>
  </div>
</template>

<script>
import apiService from "@/services/ApiService";
import FlutterBridge from "@/utils/FlutterBridge";
import * as faceapi from "@vladmandic/face-api";

const VIDEO_MIME_CANDIDATES = [
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
  "video/mp4",
];

// Max time for the WHOLE attempt (all challenges + recording) - DISABLED
// const MAX_RECORDING_MS = Infinity; // NEVER timeout - let user take their time
const CHALLENGE_COUNT = 4; // All 4 sides for security!

// Length of the actual recording
const RECORDING_DURATION_MS = 3000;
// Good frames needed before passive recording is armed
const GOOD_FRAMES_THRESHOLD = 3;
// Good frames needed to establish the baseline (active mode) - FAST!
const BASELINE_FRAMES_THRESHOLD = 3; // 3 frames = ~0.5 seconds

// Oval geometry — MUST match the <ellipse> / viewBox in the template
const OVAL = { w: 200, h: 260, rx: 72, ry: 96 };

// ── Face detection (face-api) ──
const DETECTOR_OPTIONS = { inputSize: 320, scoreThreshold: 0.5 };
const DETECT_INTERVAL_MS = 150;
// Face must be inside the oval: centre distance (1 = oval edge)
const MAX_CENTER_DIST = 0.45;
// Face height as a fraction of oval height
const MIN_FACE_RATIO = 0.5;
const MAX_FACE_RATIO = 1.0;

// ── Head pose thresholds (ratios, normalised by eye distance) ──
const YAW_THRESHOLD = 0.12; // left / right
const PITCH_THRESHOLD = 0.07; // up / down
const CENTER_YAW_TOL = 0.06;
const CENTER_PITCH_TOL = 0.05;
const BASELINE_MAX_ABS_YAW = 0.1; // face must be roughly frontal to calibrate
const CHALLENGE_FRAMES = 2; // Just 2 consecutive frames - faster!
const CHALLENGE_TIMEOUT_MS = Infinity; // NEVER timeout - wait forever
const CENTER_TIMEOUT_MS = Infinity; // NEVER timeout - wait forever
const FACE_LOST_GRACE_MS = Infinity; // NEVER cancel - wait forever for face to return
const RECORDING_BAD_TICKS_LIMIT = 3; // ~450ms off-oval cancels recording

// "left"/"right" are from the USER's point of view (what they see in the
// mirrored preview). In the raw video the user's left is image-right, so
// yaw increases when the user turns left.
const CHALLENGE_POOL = [
  {
    key: "left",
    direction: "left",
    vertical: false,
    text: "Turn your head left",
  },
  {
    key: "right",
    direction: "right",
    vertical: false,
    text: "Turn your head right",
  },
  { key: "up", direction: "up", vertical: true, text: "Tilt your head up" },
  {
    key: "down",
    direction: "down",
    vertical: true,
    text: "Tilt your head down",
  },
];

function challengeMet(direction, dYaw, dPitch) {
  switch (direction) {
    case "left":
      return dYaw > YAW_THRESHOLD;
    case "right":
      return dYaw < -YAW_THRESHOLD;
    case "up":
      return dPitch < -PITCH_THRESHOLD;
    case "down":
      return dPitch > PITCH_THRESHOLD;
    default:
      return false;
  }
}

function isIOS() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function pickSupportedMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  return (
    VIDEO_MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) || null
  );
}

function centerOf(points) {
  const s = points.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), {
    x: 0,
    y: 0,
  });
  return { x: s.x / points.length, y: s.y / points.length };
}

export default {
  name: "BiometricAuthWidget",

  props: {
    mode: {
      type: String,
      default: "login",
      validator: (v) => ["login", "register"].includes(v),
    },
    debug: { type: Boolean, default: false },
    config: {
      type: Object,
      default: () => ({
        theme: {
          primaryColor: "#007AFF",
          successColor: "#34C759",
          errorColor: "#FF3B30",
          backgroundColor: "#000000",
          textColor: "#FFFFFF",
        },
        logo: null,
        onSuccess: () => {},
        onError: () => {},
        onFallback: () => {},
        onCancel: () => {},
      }),
    },
  },

  data() {
    return {
      state: "permission-request",
      internalMode: this.mode,

      stream: null,
      requestingPermission: false,

      faceDetected: false,
      faceCenteredInOval: false,
      faceWarning: "",
      detectionInterval: null,
      recordingStartTime: 0,
      recordingTimeLeft: Math.ceil(RECORDING_DURATION_MS / 1000),
      recordingProgressPercent: 0, // Reactive progress for smooth bar

      capturedData: null,
      captureProgress: 0,
      capturePrompt: "Look straight ahead",
      mediaRecorder: null,
      recordedChunks: [],
      recordedMimeType: null,
      captureWasCancelled: false,
      sequenceRunning: false,

      successMessage: "",
      errorMessage: "",

      livenessMode: "loading",
      biometricStatus: null,
      statusError: null,
      companyName: "",

      goodFramesCount: 0,
      passiveRecordingArmed: false,

      // Active liveness
      // phases: baseline → ready → center ⇄ challenge → final-hold
      baselineFrames: 0,
      baselineYaw: 0,
      baselinePitch: 0,
      baselineEstablished: false,
      currentChallenges: [],
      currentChallengeIndex: -1,
      challengePhase: "baseline",
      edgeHighlightDirection: null,
      autoStartTriggered: false, // Prevents multiple auto-starts

      // Face-API
      faceApiModelsLoaded: false,
      pose: null, // { yaw, pitch, stamp } — null when no face
      badTicks: 0,

      // ─── NEW: Quality & Diagnostics ───
      qualityScore: 0,
      lightingScore: 0,
      sharpnessScore: 0,
      faceSizeScore: 0,
      showQualityIndicators: false,
      preflightPassed: false,
      preflightChecks: {
        camera: false,
        lighting: false,
        faceDetection: false,
        network: false,
      },

      // ─── NEW: Accessibility ───
      prefersReducedMotion: false,
      prefersHighContrast: false,
      announcements: [], // For screen readers
      audioEnabled: false,
      audioContext: null,

      // ─── NEW: Edge Cases ───
      multipleFacesDetected: false,
      faceCount: 0,
      poorLighting: false,
      orientationLocked: false,
      browserSupported: true,
      browserWarnings: [],

      // ─── NEW: Progress Persistence ───
      sessionId: null,
      progressSaved: false,
      resumedSession: false,
    };
  },

  computed: {
    cssVars() {
      const t = this.config.theme || {};
      return {
        "--primary-color": t.primaryColor || "#007AFF",
        "--success-color": t.successColor || "#34C759",
        "--error-color": t.errorColor || "#FF3B30",
        "--bg-color": t.backgroundColor || "#000000",
        "--text-color": t.textColor || "#FFFFFF",
      };
    },

    platformInstructions() {
      if (isIOS()) {
        return {
          step1: "1. Open Settings → Safari",
          step2: "2. Tap Camera → Allow",
          step3: "3. Come back and tap Try again",
        };
      }
      return {
        step1: "1. Tap the lock icon in the address bar",
        step2: "2. Open Permissions → set Camera to Allow",
        step3: "3. Reload the page or tap Try again",
      };
    },

    inChallengeSequence() {
      return (
        this.livenessMode === "active" &&
        ["center", "challenge", "final-hold"].includes(this.challengePhase)
      );
    },

    showProgress() {
      return this.state === "capturing" || this.inChallengeSequence;
    },

    canStartCapture() {
      if (this.livenessMode === "passive") return false;
      if (this.livenessMode === "active") {
        return (
          this.faceDetected &&
          this.faceCenteredInOval &&
          this.baselineEstablished &&
          this.challengePhase === "ready"
        );
      }
      return this.faceDetected && this.faceCenteredInOval;
    },

    showCaptureButton() {
      // Never show button in active mode (it auto-starts)
      if (this.livenessMode === "active") return false;
      // Never show in passive mode (it auto-starts too)
      if (this.livenessMode === "passive") return false;
      if (this.state !== "camera-preview") return false;
      return true;
    },

    positioningInstruction() {
      if (this.state === "capturing") {
        return "Keep your face inside the oval during recording";
      }
      if (this.livenessMode === "active") {
        switch (this.challengePhase) {
          case "baseline":
            return "Position your face in the center and look straight ahead";
          case "ready":
            return "Tap the button when you're ready";
          case "center":
            return "Look straight at the camera";
          case "challenge": {
            const ch = this.currentChallenges[this.currentChallengeIndex];
            return ch ? ch.text : "Please position your face clearly in view";
          }
          case "final-hold":
            return "Hold your head straight for recording";
        }
      }
      if (this.livenessMode === "passive") {
        if (this.passiveRecordingArmed) return "Great! Hold still.";
        return this.faceWarning || "Position your face clearly in the oval";
      }
      return "Position your face inside the oval to start";
    },

    captureButtonLabel() {
      if (this.livenessMode === "active") {
        if (!this.baselineEstablished) return "Calibrating… look straight";
        return this.internalMode === "login"
          ? "Start liveness check to log in"
          : "Start liveness check to register";
      }
      return this.internalMode === "login"
        ? "Start recording to log in"
        : "Start recording to register";
    },

    bottomHint() {
      // During recording - show simple progress
      if (this.state === "capturing") {
        return this.internalMode === "login"
          ? `🔴 Recording… ${this.recordingTimeLeft}s remaining`
          : `🔴 Recording… ${this.recordingTimeLeft}s remaining`;
      }

      // ─── NEW: Show warnings for edge cases (multiple faces, browser issues) ───
      if (this.multipleFacesDetected) {
        return `⚠️ ${this.faceCount} faces detected - Please ensure you're alone`;
      }
      if (!this.browserSupported && this.browserWarnings.length > 0) {
        return `🚫 ${this.browserWarnings[0]} - Please upgrade your browser`;
      }
      if (this.resumedSession) {
        return `↻ Session resumed - Continue from where you left off`;
      }

      if (this.livenessMode === "active") {
        // Show face warnings at the bottom
        if (this.faceWarning) return "⚠️ " + this.faceWarning;

        // Show challenge instructions at the bottom
        if (this.challengePhase === "center") {
          const completed = this.currentChallenges.filter(
            (c) => c.completed,
          ).length;
          if (completed > 0) {
            return `✅ Good! Now look straight at the camera (${completed}/${this.currentChallenges.length} done)`;
          }
          return "👁️ Look straight at the camera";
        }
        if (this.challengePhase === "challenge") {
          const ch = this.currentChallenges[this.currentChallengeIndex];
          if (ch) {
            const icons = { left: "⬅️", right: "➡️", up: "⬆️", down: "⬇️" };
            const progress = `(${this.currentChallengeIndex + 1}/${
              this.currentChallenges.length
            })`;
            return `${icons[ch.direction] || "🔄"} ${ch.text} ${progress}`;
          }
        }
        if (this.challengePhase === "final-hold") {
          return "✋ Hold your head straight for recording";
        }

        if (this.inChallengeSequence) return "Follow the instructions above";
        if (!this.baselineEstablished) {
          return "📸 Hold still — calibrating your position…";
        }
        if (!this.canStartCapture)
          return "⚠️ Align your face fully inside the oval";

        // ─── NEW: Show quality hint when ready ───
        if (this.showQualityIndicators && this.qualityScore > 0) {
          return `✅ Quality: ${this.qualityLevel.text} ${this.lightingLevel.icon} - Ready!`;
        }
        return "✅ Get ready — liveness check starting automatically…";
      }

      if (this.livenessMode === "passive") {
        if (this.faceWarning) return "⚠️ " + this.faceWarning;
        if (this.passiveRecordingArmed)
          return "✅ Keep holding — almost there…";
        if (!this.faceDetected || !this.faceCenteredInOval) {
          return "⚠️ Align your face fully inside the oval";
        }
        // ─── NEW: Show quality hint in passive mode ───
        if (this.showQualityIndicators && this.qualityScore > 0) {
          return `📸 Quality: ${this.qualityLevel.text} ${this.lightingLevel.icon} - Hold still`;
        }
        return "📸 Keep your face centered and hold still";
      }

      if (this.faceWarning) return "⚠️ " + this.faceWarning;
      if (!this.canStartCapture)
        return "⚠️ Align your face fully inside the oval";

      // ─── NEW: Show quality hint when camera is ready ───
      if (this.showQualityIndicators && this.qualityScore > 0) {
        return `${this.lightingLevel.icon} Lighting: ${this.lightingLevel.text} - ${this.qualityLevel.text} quality`;
      }

      return this.internalMode === "login"
        ? "Hold still and look at the camera"
        : "Ready to record your face video";
    },

    faceBadgeText() {
      // Face presence always wins — never show "follow instruction" with no face
      if (!this.faceDetected) return "No face detected";
      if (this.livenessMode === "active") {
        if (this.challengePhase === "baseline" && !this.baselineEstablished) {
          return "Keep still — calibrating…";
        }
        if (this.challengePhase === "center") return "Return to center";
        if (this.challengePhase === "challenge") {
          return "Follow the instruction above";
        }
        if (this.challengePhase === "final-hold") {
          return "Almost there — hold position";
        }
      }
      if (this.livenessMode === "passive" && this.passiveRecordingArmed) {
        return "✓ Position good";
      }
      if (this.livenessMode === "passive" && this.goodFramesCount > 0) {
        return "Keep holding position…";
      }
      if (this.faceCenteredInOval) return "✓ Face aligned in oval";
      if (this.faceWarning) return "⚠ " + this.faceWarning;
      return "Move face fully inside oval";
    },

    ovalStrokeColor() {
      if (this.faceDetected && this.faceCenteredInOval) {
        return "var(--success-color)";
      }
      if (this.faceWarning) return "var(--error-color)";
      return "rgba(255,255,255,0.7)";
    },

    // ─── NEW: Quality Indicators ───
    qualityLevel() {
      if (this.qualityScore >= 90)
        return { level: "excellent", color: "#34C759", text: "Excellent" };
      if (this.qualityScore >= 75)
        return { level: "good", color: "#34C759", text: "Good" };
      if (this.qualityScore >= 50)
        return { level: "fair", color: "#FF9500", text: "Fair" };
      return { level: "poor", color: "#FF3B30", text: "Poor" };
    },

    lightingLevel() {
      if (this.lightingScore >= 80)
        return { text: "Great lighting", icon: "☀️", color: "#34C759" };
      if (this.lightingScore >= 50)
        return { text: "Adequate lighting", icon: "🌤️", color: "#FF9500" };
      return { text: "Poor lighting", icon: "🌑", color: "#FF3B30" };
    },

    preflightStatus() {
      const total = Object.keys(this.preflightChecks).length;
      const passed = Object.values(this.preflightChecks).filter(Boolean).length;
      return { passed, total, percentage: (passed / total) * 100 };
    },

    accessibilityAnnouncement() {
      if (this.announcements.length === 0) return "";
      return this.announcements[this.announcements.length - 1];
    },
  },

  watch: {
    mode(v) {
      this.internalMode = v;
    },
  },

  async mounted() {
    FlutterBridge.notifyLoaded();
    FlutterBridge.sendReady();

    // ─── NEW: Initialize new features ───
    this.initializeAccessibility();
    this.checkBrowserCompatibility();
    this.lockOrientation();
    this.loadSavedProgress();
    this.generateSessionId();

    await this.loadFaceApiModels();
    await this.fetchBiometricStatus();
    this.requestCameraPermission();
  },

  beforeUnmount() {
    this.stopCamera();
    this.saveProgress();
    if (this.audioContext) {
      this.audioContext.close();
    }
  },

  methods: {
    log(...a) {
      if (this.debug) console.log("[biometric]", ...a);
    },

    async loadFaceApiModels() {
      if (this.faceApiModelsLoaded) return true;
      try {
        const modelPath = "/models";
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(modelPath),
          faceapi.nets.faceLandmark68Net.loadFromUri(modelPath),
        ]);
        this.faceApiModelsLoaded = true;
        this.log("Face-api models loaded");
      } catch (err) {
        console.error("[FACE-API] Failed to load models:", err);
        this.faceApiModelsLoaded = false;
      }
      return this.faceApiModelsLoaded;
    },

    _getTokenFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return params.get("token");
    },

    async fetchBiometricStatus() {
      const token = this._getTokenFromUrl();
      if (!token) {
        this.livenessMode = "passive";
        return;
      }
      try {
        const res = await apiService.getBiometricStatus(token);
        this.biometricStatus = res.data;
        this.companyName = res.data.company_name || "";
        if (res.data.biometric_active_liveness === true) {
          this.livenessMode = "active";
          this.currentChallenges = this.generateChallenges();
          this.challengePhase = "baseline";
        } else {
          this.livenessMode = "passive";
        }
      } catch (err) {
        this.statusError =
          err?.data?.message || err?.message || "Failed to fetch status";
        this.livenessMode = "passive";
      }
    },

    generateChallenges() {
      const pool = [...CHALLENGE_POOL];
      const selected = [];
      let lastVertical = null;

      for (let i = 0; i < CHALLENGE_COUNT && pool.length > 0; i++) {
        let idx = Math.floor(Math.random() * pool.length);
        let attempt = 0;
        while (
          pool[idx] &&
          lastVertical !== null &&
          pool[idx].vertical === lastVertical &&
          attempt < pool.length
        ) {
          idx = (idx + 1) % pool.length;
          attempt++;
        }
        const ch = pool.splice(idx, 1)[0];
        lastVertical = ch.vertical;
        // Copy so `completed` never leaks into the shared pool
        selected.push({ ...ch, completed: false });
      }
      this.log(
        "Challenges:",
        selected.map((c) => c.key),
      );
      return selected;
    },

    resetActiveLivenessState() {
      this.baselineFrames = 0;
      this.baselineYaw = 0;
      this.baselinePitch = 0;
      this.baselineEstablished = false;
      this.currentChallengeIndex = -1;
      this.challengePhase = "baseline";
      this.edgeHighlightDirection = null;
      this.pose = null;
      this.currentChallenges = this.generateChallenges();
      this.autoStartTriggered = false; // Reset auto-start flag
      this.captureWasCancelled = false; // Reset cancel flag too
    },

    resetPassiveLivenessState() {
      this.goodFramesCount = 0;
      this.passiveRecordingArmed = false;
    },

    // ─── Events / bridge ─────────────────────────────────────────────────

    emit(event, payload) {
      this.config[event]?.(payload);

      if (event === "onSuccess") FlutterBridge.sendSuccess(payload);
      else if (event === "onError") FlutterBridge.sendError(payload);
      else if (event === "onFallback") FlutterBridge.sendFallback(payload);
      else if (event === "onCancel") FlutterBridge.sendCancel(payload);

      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: `biometric-${event.replace("on", "").toLowerCase()}`,
            data: payload,
            timestamp: new Date().toISOString(),
          },
          "*",
        );
      }

      if (window.BiometricChannel) {
        window.BiometricChannel.postMessage(JSON.stringify({ event, payload }));
      }
    },

    switchMode(mode) {
      this.internalMode = mode;
      // Stop any active recording first
      if (this.state === "capturing" || this.sequenceRunning) {
        this.captureWasCancelled = true;
        this.sequenceRunning = false;
        this.clearRecordingTimers();
        if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
          this.mediaRecorder.stop();
        }
        this.mediaRecorder = null;
        this.state = "camera-preview";
      }
      // Reset states AFTER stopping recording so flags are fresh
      this.resetPassiveLivenessState();
      this.resetActiveLivenessState();

      // ─── NEW: Feedback on mode switch ───
      this.playClickSound();
      this.vibrateClick();
      this.announce(`Switched to ${mode} mode`);
    },

    // ─── Camera ──────────────────────────────────────────────────────────

    async requestCameraPermission() {
      if (this.requestingPermission) return;
      this.requestingPermission = true;

      if (!window.isSecureContext) {
        this.state = "insecure-context";
        this.requestingPermission = false;
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        this.state = "no-camera";
        this.requestingPermission = false;
        return;
      }

      // Face detection is required — never run liveness without it
      if (!(await this.loadFaceApiModels())) {
        this.errorMessage =
          "Face detection could not be loaded. Check your connection and try again.";
        this.state = "failure";
        this.requestingPermission = false;
        this.config.onError?.({ type: "face_models_load_error" });
        return;
      }

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        this.state = "camera-preview";
        this.resetPassiveLivenessState();
        this.resetActiveLivenessState();
        await this.$nextTick();

        const video = this.$refs.videoElement;
        if (video) {
          video.srcObject = this.stream;
          try {
            await video.play();
          } catch (_) {
            /* harmless on some browsers */
          }
          this.startFaceDetection();

          // ─── NEW: Run pre-flight checks after camera starts ───
          setTimeout(async () => {
            if (this.state === "camera-preview") {
              this.showQualityIndicators = true;
              await this.runPreflightChecks();
              this.announce("Camera ready. Quality indicators displayed.");
            }
          }, 2000); // Wait 2 seconds for camera to stabilize
        }
      } catch (err) {
        this.log("Camera error", err.name, err.message);
        if (
          [
            "NotAllowedError",
            "PermissionDeniedError",
            "SecurityError",
          ].includes(err.name)
        ) {
          this.state = "permission-denied";
        } else if (
          [
            "NotFoundError",
            "DevicesNotFoundError",
            "OverconstrainedError",
          ].includes(err.name)
        ) {
          this.state = "no-camera";
        } else if (["NotReadableError", "TrackStartError"].includes(err.name)) {
          this.errorMessage =
            "Camera is in use by another app. Close it and try again.";
          this.state = "failure";
        } else {
          this.state = "permission-denied";
        }
        this.config.onError?.({
          type: "camera_access_error",
          error: err.message,
        });
      } finally {
        this.requestingPermission = false;
      }
    },

    // ─── Detection loop ──────────────────────────────────────────────────

    startFaceDetection() {
      this.clearDetectionInterval();
      this.detectionInterval = setInterval(async () => {
        if (this._ticking) return; // previous detection still running
        this._ticking = true;
        try {
          await this.runFaceDetection();
          if (this.state === "camera-preview") {
            if (this.livenessMode === "passive") {
              this.processPassiveLivenessTick();
            } else if (this.livenessMode === "active") {
              this.processActiveLivenessTick();
            }
          } else if (this.state === "capturing") {
            this.validateDuringRecording();
          }
        } finally {
          this._ticking = false;
        }
      }, DETECT_INTERVAL_MS);
    },

    /**
     * Real face detection + landmarks (face-api). Sets faceDetected,
     * faceCenteredInOval, faceWarning and pose { yaw, pitch }.
     */
    async runFaceDetection() {
      const v = this.$refs.videoElement;
      const screen = this.$refs.cameraScreen;
      const wrap = this.$refs.faceWrap;
      if (!v || !screen || !wrap || v.readyState < 2 || !v.videoWidth) return;

      let det = null;
      try {
        det = await faceapi
          .detectSingleFace(
            v,
            new faceapi.TinyFaceDetectorOptions(DETECTOR_OPTIONS),
          )
          .withFaceLandmarks();
      } catch (_) {
        det = null;
      }

      // ─── NEW: Assess quality every few frames ───
      if (this._detectionTicks % 5 === 0) {
        await this.assessQuality();
      }
      this._detectionTicks = (this._detectionTicks || 0) + 1;

      if (!det) {
        this.faceDetected = false;
        this.faceCenteredInOval = false;
        this.faceWarning = "";
        this.pose = null;
        this.faceCount = 0;
        this.multipleFacesDetected = false;
        return;
      }

      // ─── NEW: Check for multiple faces (would need detectAllFaces) ───
      // For now we use single face detection, but this is the hook for future enhancement
      this.faceCount = 1;
      this.multipleFacesDetected = false;

      // ── Map face box from raw video → on-screen (object-fit: cover + mirror)
      const sr = screen.getBoundingClientRect();
      const wr = wrap.getBoundingClientRect();
      const vw = v.videoWidth;
      const vh = v.videoHeight;
      const scale = Math.max(sr.width / vw, sr.height / vh);
      const offX = (vw * scale - sr.width) / 2;
      const offY = (vh * scale - sr.height) / 2;

      const box = det.detection.box;
      const faceCx = sr.width - ((box.x + box.width / 2) * scale - offX);
      const faceCy = (box.y + box.height / 2) * scale - offY;
      const faceH = box.height * scale;

      const ovalCx = wr.left - sr.left + wr.width / 2;
      const ovalCy = wr.top - sr.top + wr.height / 2;
      const ovalRx = (wr.width * OVAL.rx) / OVAL.w;
      const ovalRy = (wr.height * OVAL.ry) / OVAL.h;

      const dx = (faceCx - ovalCx) / ovalRx;
      const dy = (faceCy - ovalCy) / ovalRy;
      const dist = Math.hypot(dx, dy);
      const sizeRatio = faceH / (2 * ovalRy);

      let warning = "";

      // ─── NEW: Poor lighting warning ───
      if (this.poorLighting && this.lightingScore < 40) {
        warning = "Lighting too dark - move to brighter area";
      } else if (this.poorLighting && this.lightingScore > 90) {
        warning = "Too much glare - reduce bright lights";
      } else if (sizeRatio < MIN_FACE_RATIO) {
        warning = "Move closer to the camera";
      } else if (sizeRatio > MAX_FACE_RATIO) {
        warning = "Move back a little";
      } else if (dist > MAX_CENTER_DIST) {
        warning = "Center your face in the oval";
      }

      this.faceDetected = true;
      this.faceCenteredInOval = warning === "";
      this.faceWarning = warning;

      // ── Head pose from landmarks (normalised by eye distance)
      const lm = det.landmarks;
      const le = centerOf(lm.getLeftEye());
      const re = centerOf(lm.getRightEye());
      const nose = lm.positions[30];
      const eyeDist = Math.hypot(re.x - le.x, re.y - le.y) || 1;
      const eyeMidX = (le.x + re.x) / 2;
      const eyeMidY = (le.y + re.y) / 2;

      this.pose = {
        yaw: (nose.x - eyeMidX) / eyeDist, // + = user's left
        pitch: (nose.y - eyeMidY) / eyeDist, // + = down
        stamp: performance.now(), // unique per detection
      };
    },

    processPassiveLivenessTick() {
      if (this.faceDetected && this.faceCenteredInOval && !this.faceWarning) {
        this.goodFramesCount++;
        if (
          this.goodFramesCount >= GOOD_FRAMES_THRESHOLD &&
          !this.passiveRecordingArmed
        ) {
          this.passiveRecordingArmed = true;
          setTimeout(() => {
            if (
              this.state === "camera-preview" &&
              this.faceDetected &&
              this.faceCenteredInOval &&
              this.passiveRecordingArmed
            ) {
              this.startVideoCapture();
            } else {
              this.passiveRecordingArmed = false;
              this.goodFramesCount = 0;
            }
          }, 500);
        }
      } else {
        this.goodFramesCount = 0;
        this.passiveRecordingArmed = false;
      }
    },

    /** Baseline calibration + keeping the baseline fresh while idle. */
    processActiveLivenessTick() {
      const good =
        this.faceDetected &&
        this.faceCenteredInOval &&
        !this.faceWarning &&
        this.pose;

      if (this.challengePhase === "baseline") {
        if (good && Math.abs(this.pose.yaw) < BASELINE_MAX_ABS_YAW) {
          const n = this.baselineFrames;
          this.baselineYaw = (this.baselineYaw * n + this.pose.yaw) / (n + 1);
          this.baselinePitch =
            (this.baselinePitch * n + this.pose.pitch) / (n + 1);
          this.baselineFrames++;
          if (this.baselineFrames >= BASELINE_FRAMES_THRESHOLD) {
            this.baselineEstablished = true;
            this.challengePhase = "ready";
            this.log(
              "Baseline",
              this.baselineYaw.toFixed(3),
              this.baselinePitch.toFixed(3),
            );
          }
        } else {
          // Not frontal / face lost → restart calibration
          this.baselineFrames = 0;
          this.baselineYaw = 0;
          this.baselinePitch = 0;
        }
      } else if (this.challengePhase === "ready" && good) {
        // Slowly follow the user's resting pose until they press start
        const dYaw = this.pose.yaw - this.baselineYaw;
        if (Math.abs(dYaw) < 0.08) {
          this.baselineYaw += 0.1 * dYaw;
          this.baselinePitch += 0.1 * (this.pose.pitch - this.baselinePitch);
        }

        // AUTO-START: Automatically start liveness check when face is ready
        if (
          !this.autoStartTriggered &&
          !this.sequenceRunning &&
          !this.captureWasCancelled
        ) {
          this.autoStartTriggered = true; // Prevent multiple triggers
          this.log("Auto-starting liveness check - face is ready!");
          setTimeout(() => {
            if (
              this.challengePhase === "ready" &&
              this.canStartCapture &&
              !this.sequenceRunning
            ) {
              this.startVideoCapture();
            }
          }, 300); // 300ms to ensure face is stable
        }
      }
    },

    clearDetectionInterval() {
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval);
        this.detectionInterval = null;
      }
    },

    clearRecordingTimers() {
      if (this._countdownInterval) {
        clearInterval(this._countdownInterval);
        this._countdownInterval = null;
      }
      if (this._timeout) {
        clearTimeout(this._timeout);
        this._timeout = null;
      }
    },

    failLiveness(message, type = "liveness_failed") {
      this.captureWasCancelled = true;
      this.sequenceRunning = false;
      this.clearRecordingTimers();
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
      this.errorMessage = message;
      this.state = "failure";
      this.stopCamera();

      // ─── NEW: Error feedback ───
      this.playErrorSound();
      this.vibrateError();
      this.announce(`Error: ${message}`);

      this.config.onError?.({ type, mode: this.internalMode });
    },

    validateDuringRecording() {
      if (!this.faceDetected || !this.faceCenteredInOval) {
        this.badTicks++;
      } else {
        this.badTicks = 0;
      }
      if (this.badTicks < RECORDING_BAD_TICKS_LIMIT) return;

      this.captureWasCancelled = true;
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
      this.failLiveness(
        "Recording cancelled: your face left the oval. Keep your face centered and try again.",
        "face_left_oval_during_recording",
      );
    },

    // ─── Capture ─────────────────────────────────────────────────────────

    async startVideoCapture() {
      if (this.sequenceRunning || this.state === "capturing") return;
      const isActive = this.livenessMode === "active";

      if (isActive && !this.canStartCapture) return;

      const mimeType = pickSupportedMimeType();
      if (!mimeType) {
        this.errorMessage =
          "Video recording isn't supported on this browser. Try latest Chrome or Safari.";
        this.state = "failure";
        this.config.onError?.({ type: "unsupported_media_recorder" });
        return;
      }

      this.sequenceRunning = true;
      this.captureProgress = 0;
      this.recordedChunks = [];
      this.recordedMimeType = mimeType;
      this.captureWasCancelled = false;
      this.badTicks = 0;

      try {
        this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });

        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) this.recordedChunks.push(e.data);
        };

        this.mediaRecorder.onstop = async () => {
          this.clearRecordingTimers();
          if (this.captureWasCancelled) return;
          this.capturedData = new Blob(this.recordedChunks, {
            type: this.recordedMimeType,
          });
          this.capturePrompt = "Processing video…";
          this.state = "processing";
          this.clearDetectionInterval();
          await this.submitBiometricData();
        };

        // Overall time limit for the whole attempt - DISABLED to let user take their time
        this.clearRecordingTimers();
        // No timeout - let the user complete challenges at their own pace
        // this._timeout = setTimeout(() => {
        //   if (this.state === "camera-preview" || this.state === "capturing") {
        //     this.failLiveness(
        //       "Time ran out. Please follow the prompts and try again.",
        //       "liveness_timeout",
        //     );
        //   }
        // }, MAX_RECORDING_MS);

        if (isActive) {
          const ok = await this.runActiveChallengeSequence();
          if (!ok || this.captureWasCancelled) {
            this.sequenceRunning = false;
            return;
          }
        }

        this.state = "capturing";
        this.recordingStartTime = Date.now();
        this.recordingTimeLeft = Math.ceil(RECORDING_DURATION_MS / 1000);
        this.captureProgress = 0;
        this.mediaRecorder.start();

        this._countdownInterval = setInterval(() => {
          const elapsed = Date.now() - this.recordingStartTime;
          const remaining = Math.max(0, RECORDING_DURATION_MS - elapsed);
          this.recordingTimeLeft = Math.ceil(remaining / 1000);
          // Update progress bar percentage
          this.recordingProgressPercent = Math.min(
            100,
            (elapsed / RECORDING_DURATION_MS) * 100,
          );
        }, 200);

        const prompts = isActive
          ? [
              { text: "Hold still — recording…", duration: 1000 },
              { text: "Almost there…", duration: 1000 },
              { text: "Great! Hold still.", duration: 1000 },
            ]
          : [
              { text: "Great! Hold still.", duration: 1000 },
              { text: "Recording… Hold still.", duration: 1000 },
              { text: "Recording… Hold still.", duration: 1000 },
            ];

        let elapsed = 0;
        for (const p of prompts) {
          if (this.captureWasCancelled || this.state !== "capturing") break;
          this.capturePrompt = p.text;
          await this.sleep(p.duration);
          elapsed += p.duration;
          this.captureProgress = Math.round(
            (elapsed / RECORDING_DURATION_MS) * 100,
          );
        }

        this.sequenceRunning = false;
        if (this.captureWasCancelled || this.state !== "capturing") return;
        this.captureProgress = 100;
        if (this.mediaRecorder?.state === "recording") {
          this.mediaRecorder.stop(); // onstop -> submitBiometricData()
        }
      } catch (err) {
        this.log("Video capture error", err);
        this.sequenceRunning = false;
        this.errorMessage = "Failed to record video";
        this.state = "failure";
      }
    },

    /**
     * Wait until check(dYaw, dPitch) holds for `needFrames` consecutive NEW
     * face detections. Fails if the face disappears for too long.
     * Returns { ok, reason }.
     */
    async waitForPose(check, timeoutMs, needFrames) {
      const start = Date.now();
      let good = 0;
      let lastStamp = 0;
      let lostSince = null;

      while (Date.now() - start < timeoutMs) {
        if (this.captureWasCancelled) return { ok: false, reason: "cancelled" };

        const p = this.pose;
        if (!this.faceDetected || !p) {
          good = 0;
          lostSince = lostSince || Date.now();
          if (Date.now() - lostSince > FACE_LOST_GRACE_MS) {
            return { ok: false, reason: "face-lost" };
          }
        } else {
          lostSince = null;
          if (p.stamp !== lastStamp) {
            lastStamp = p.stamp;
            const dYaw = p.yaw - this.baselineYaw;
            const dPitch = p.pitch - this.baselinePitch;
            if (check(dYaw, dPitch)) {
              good++;
              if (good >= needFrames) return { ok: true };
            } else {
              good = 0;
            }
          }
        }
        await this.sleep(50);
      }
      return { ok: false, reason: "timeout" };
    },

    _failFromReason(reason, challenge) {
      if (reason === "cancelled") return;
      if (reason === "face-lost") {
        this.failLiveness(
          "We lost sight of your face. Keep your face in the camera view and try again.",
          "face_lost_during_liveness",
        );
      } else if (challenge) {
        this.failLiveness(
          `Liveness check failed: we didn't detect your head turning ${challenge.direction}. Please try again.`,
          "liveness_challenge_failed",
        );
      } else {
        this.failLiveness(
          "Liveness check failed: please look straight at the camera and try again.",
          "liveness_center_failed",
        );
      }
    },

    /** Returns true only if every challenge was verified from real landmarks. */
    async runActiveChallengeSequence() {
      const total = this.currentChallenges.length;
      const stepPct = 100 / (total + 1);

      for (let i = 0; i < total; i++) {
        const challenge = this.currentChallenges[i];
        this.currentChallengeIndex = i;

        // 1) Come back to centre first
        this.challengePhase = "center";
        this.edgeHighlightDirection = null;
        this.capturePrompt = "Look straight at the camera";
        let res = await this.waitForPose(
          (dy, dp) =>
            Math.abs(dy) < CENTER_YAW_TOL && Math.abs(dp) < CENTER_PITCH_TOL,
          CENTER_TIMEOUT_MS,
          1, // Just 1 frame - instant!
        );
        if (!res.ok) {
          this._failFromReason(res.reason, null);
          return false;
        }

        // 2) The actual movement
        this.challengePhase = "challenge";
        this.edgeHighlightDirection = challenge.direction;
        this.capturePrompt = challenge.text;
        res = await this.waitForPose(
          (dy, dp) => challengeMet(challenge.direction, dy, dp),
          CHALLENGE_TIMEOUT_MS,
          CHALLENGE_FRAMES,
        );
        if (!res.ok) {
          this._failFromReason(res.reason, challenge);
          return false;
        }

        challenge.completed = true;
        this.captureProgress = Math.round((i + 1) * stepPct);
        this.log(
          `Challenge ${i + 1}/${total} verified: ${challenge.direction}`,
        );

        // ─── NEW: Feedback on challenge completion ───
        this.playSuccessSound();
        this.vibrateSuccess();
        this.announce(
          `Challenge ${i + 1} of ${total} completed: ${challenge.text}`,
        );
      }

      // 3) Final hold: straight, centred, before recording starts
      this.challengePhase = "final-hold";
      this.edgeHighlightDirection = null;
      this.capturePrompt = "Hold your head straight for recording";
      const hold = await this.waitForPose(
        (dy, dp) =>
          Math.abs(dy) < CENTER_YAW_TOL && Math.abs(dp) < CENTER_PITCH_TOL,
        CENTER_TIMEOUT_MS,
        2, // Just 2 frames - faster!
      );
      if (!hold.ok) {
        this._failFromReason(hold.reason, null);
        return false;
      }
      if (!this.faceCenteredInOval) {
        this.failLiveness(
          "Keep your face inside the oval and try again.",
          "face_not_in_oval",
        );
        return false;
      }

      return this.currentChallenges.every((c) => c.completed === true);
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result.split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    },

    async submitBiometricData() {
      try {
        const base64Data = await this.blobToBase64(this.capturedData);
        const filename = this.recordedMimeType?.includes("mp4")
          ? "capture.mp4"
          : "capture.webm";

        const challenges =
          this.livenessMode === "active" ? this.currentChallenges : [];
        const allChallengesVerified =
          challenges.length > 0 &&
          challenges.every((c) => c.completed === true);

        // NOTE: this is informational only. The backend must verify liveness
        // itself — a client flag can be forged.
        const payload = {
          filename,
          face_template: base64Data,
          liveness: {
            mode: this.livenessMode,
            challenges: challenges.map((c) => ({
              direction: c.direction,
              verified: c.completed === true,
            })),
            client_verified: allChallengesVerified,
            timestamp: new Date().toISOString(),
          },
        };

        const data = await (this.internalMode === "login"
          ? apiService.loginBiometric(payload)
          : apiService.registerBiometric(payload));

        if (data.status === 200) {
          this.successMessage =
            data.message ||
            (this.internalMode === "login"
              ? "You have been successfully authenticated"
              : "Your face has been registered successfully");
          this.state = "success";
          this.stopCamera();

          // ─── NEW: Success feedback ───
          this.playSuccessSound();
          this.vibrateSuccess();
          this.announce(`Success! ${this.successMessage}`);
          this.clearSavedProgress();

          this.config.onSuccess?.({ mode: this.internalMode, data });
        } else {
          throw new Error(data.message || "Biometric authentication failed");
        }
      } catch (err) {
        this.errorMessage =
          err.data?.message ||
          err.message ||
          "An error occurred during processing";
        this.state = "failure";
        this.stopCamera();
        this.config.onError?.({
          type: "submission_error",
          mode: this.internalMode,
          error: err.message,
        });
      }
    },

    cancelCapture() {
      this.captureWasCancelled = true;
      this.sequenceRunning = false;
      this.stopCamera();
      this.resetPassiveLivenessState();
      this.resetActiveLivenessState();
      this.config.onCancel?.({ mode: this.internalMode });
      this.$nextTick(() => this.requestCameraPermission());
    },

    retry() {
      this.errorMessage = "";
      this.capturedData = null;
      this.captureWasCancelled = false;
      this.sequenceRunning = false;
      this.resetPassiveLivenessState();
      this.resetActiveLivenessState();
      this.requestCameraPermission();
    },

    stopCamera() {
      this.clearRecordingTimers();
      this.clearDetectionInterval();
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive")
        this.mediaRecorder.stop();
      this.mediaRecorder = null;
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
      this.faceDetected = false;
      this.faceCenteredInOval = false;
      this.faceWarning = "";
      this.pose = null;
      this.badTicks = 0;
      this.captureProgress = 0;
    },

    handleFallback() {
      this.config.onFallback?.({ mode: this.internalMode });
    },

    sleep(ms) {
      return new Promise((r) => setTimeout(r, ms));
    },

    // ═══════════════════════════════════════════════════════════════════
    // ─── NEW FEATURES: Quality, Accessibility, Edge Cases ───
    // ═══════════════════════════════════════════════════════════════════

    // ─── Accessibility ───────────────────────────────────────────────

    initializeAccessibility() {
      // Detect user preferences
      this.prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      this.prefersHighContrast = window.matchMedia(
        "(prefers-contrast: high)",
      ).matches;

      // Listen for changes
      window
        .matchMedia("(prefers-reduced-motion: reduce)")
        .addEventListener("change", (e) => {
          this.prefersReducedMotion = e.matches;
          this.announce("Motion preferences updated");
        });

      window
        .matchMedia("(prefers-contrast: high)")
        .addEventListener("change", (e) => {
          this.prefersHighContrast = e.matches;
          this.announce("Contrast mode updated");
        });

      // Setup keyboard navigation
      document.addEventListener("keydown", this.handleKeyboardNav);

      this.announce(
        "Biometric authentication widget loaded. Press Tab to navigate, Enter to activate.",
      );
    },

    handleKeyboardNav(e) {
      // Space or Enter on focusable elements
      if (
        e.key === "Escape" &&
        (this.state === "camera-preview" || this.state === "capturing")
      ) {
        this.cancelCapture();
        this.announce("Capture cancelled");
      }
      if (
        e.key === "Enter" &&
        this.canStartCapture &&
        this.state === "camera-preview"
      ) {
        this.startVideoCapture();
        this.announce("Starting biometric capture");
      }
      // Arrow keys to switch modes
      if (e.key === "ArrowLeft" && this.internalMode === "register") {
        this.switchMode("login");
        this.announce("Switched to login mode");
      }
      if (e.key === "ArrowRight" && this.internalMode === "login") {
        this.switchMode("register");
        this.announce("Switched to register mode");
      }
    },

    announce(message, priority = "polite") {
      this.announcements.push(message);
      // Keep only last 5 announcements
      if (this.announcements.length > 5) {
        this.announcements.shift();
      }
      // Emit event for screen reader (aria-live region will pick this up)
      this.$nextTick(() => {
        const region = document.querySelector(".sr-announcer");
        if (region) {
          region.setAttribute("aria-live", priority);
          region.textContent = message;
        }
      });
    },

    // ─── Audio Feedback ─────────────────────────────────────────────

    initAudio() {
      if (!this.audioContext && window.AudioContext) {
        this.audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
    },

    playTone(frequency = 800, duration = 100, type = "sine") {
      if (!this.audioEnabled || !this.audioContext) return;

      try {
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = type;

        gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          this.audioContext.currentTime + duration / 1000,
        );

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration / 1000);
      } catch (err) {
        console.warn("Audio playback failed:", err);
      }
    },

    playSuccessSound() {
      this.playTone(523.25, 100); // C5
      setTimeout(() => this.playTone(659.25, 100), 100); // E5
      setTimeout(() => this.playTone(783.99, 150), 200); // G5
    },

    playErrorSound() {
      this.playTone(300, 200, "sawtooth");
      setTimeout(() => this.playTone(250, 300, "sawtooth"), 150);
    },

    playClickSound() {
      this.playTone(1000, 50);
    },

    toggleAudio() {
      this.audioEnabled = !this.audioEnabled;
      if (this.audioEnabled) {
        this.initAudio();
        this.playClickSound();
        this.announce("Audio feedback enabled");
      } else {
        this.announce("Audio feedback disabled");
      }
    },

    // ─── Haptic Feedback ────────────────────────────────────────────

    vibrate(pattern = 200) {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    },

    vibrateSuccess() {
      this.vibrate([100, 50, 100]);
    },

    vibrateError() {
      this.vibrate([200, 100, 200, 100, 200]);
    },

    vibrateClick() {
      this.vibrate(50);
    },

    // ─── Quality Assessment ─────────────────────────────────────────

    async assessQuality() {
      const v = this.$refs.videoElement;
      if (!v || v.readyState < 2) return;

      try {
        // Create canvas for analysis
        const canvas = document.createElement("canvas");
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(v, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        // Calculate lighting (brightness)
        let brightnessSum = 0;
        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const brightness = (r + g + b) / 3;
          brightnessSum += brightness;
        }
        const avgBrightness = brightnessSum / (pixels.length / 4);

        // Lighting score: ideal range 80-180
        if (avgBrightness < 40) {
          this.lightingScore = 10;
          this.poorLighting = true;
        } else if (avgBrightness < 80) {
          this.lightingScore = 40;
          this.poorLighting = true;
        } else if (avgBrightness >= 80 && avgBrightness <= 180) {
          this.lightingScore = 100;
          this.poorLighting = false;
        } else if (avgBrightness <= 220) {
          this.lightingScore = 60;
          this.poorLighting = true;
        } else {
          this.lightingScore = 20;
          this.poorLighting = true;
        }

        // Calculate sharpness (using Laplacian variance)
        const sharpness = this.calculateSharpness(imageData);
        this.sharpnessScore = Math.min(100, Math.max(0, sharpness * 20));

        // Face size score (already calculated in detection)
        if (this.faceDetected) {
          this.faceSizeScore = this.faceCenteredInOval ? 100 : 50;
        } else {
          this.faceSizeScore = 0;
        }

        // Overall quality score
        this.qualityScore = Math.round(
          this.lightingScore * 0.4 +
            this.sharpnessScore * 0.3 +
            this.faceSizeScore * 0.3,
        );
      } catch (err) {
        console.warn("Quality assessment failed:", err);
      }
    },

    calculateSharpness(imageData) {
      // Simple Laplacian edge detection for sharpness
      const pixels = imageData.data;
      const width = imageData.width;
      let variance = 0;
      let count = 0;

      for (let y = 1; y < imageData.height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const i = (y * width + x) * 4;
          const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;

          const top =
            (pixels[(y - 1) * width * 4 + x * 4] +
              pixels[(y - 1) * width * 4 + x * 4 + 1] +
              pixels[(y - 1) * width * 4 + x * 4 + 2]) /
            3;
          const bottom =
            (pixels[(y + 1) * width * 4 + x * 4] +
              pixels[(y + 1) * width * 4 + x * 4 + 1] +
              pixels[(y + 1) * width * 4 + x * 4 + 2]) /
            3;
          const left = (pixels[i - 4] + pixels[i - 3] + pixels[i - 2]) / 3;
          const right = (pixels[i + 4] + pixels[i + 5] + pixels[i + 6]) / 3;

          const laplacian = Math.abs(4 * gray - top - bottom - left - right);
          variance += laplacian;
          count++;
        }
      }

      return variance / count;
    },

    // ─── Pre-flight Checks ──────────────────────────────────────────

    async runPreflightChecks() {
      this.announce("Running system checks");

      // Check 1: Camera
      this.preflightChecks.camera = !!this.stream;

      // Check 2: Lighting (wait for quality assessment)
      await this.assessQuality();
      this.preflightChecks.lighting = this.lightingScore >= 50;

      // Check 3: Face Detection Models
      this.preflightChecks.faceDetection = this.faceApiModelsLoaded;

      // Check 4: Network Speed (simple ping test)
      this.preflightChecks.network = await this.checkNetworkSpeed();

      this.preflightPassed = Object.values(this.preflightChecks).every(Boolean);

      if (this.preflightPassed) {
        this.announce("All checks passed. Ready to proceed.");
        this.playSuccessSound();
        this.vibrateSuccess();
      } else {
        this.announce("Some checks failed. Please improve conditions.");
        this.playErrorSound();
        this.vibrateError();
      }

      return this.preflightPassed;
    },

    async checkNetworkSpeed() {
      try {
        const start = performance.now();
        await fetch(window.location.origin, {
          method: "HEAD",
          cache: "no-store",
        });
        const duration = performance.now() - start;
        return duration < 1000; // Under 1 second is good
      } catch {
        return false;
      }
    },

    // ─── Edge Case Handling ─────────────────────────────────────────

    checkBrowserCompatibility() {
      const warnings = [];

      // Check MediaRecorder
      if (typeof MediaRecorder === "undefined") {
        warnings.push("Video recording not supported");
        this.browserSupported = false;
      }

      // Check getUserMedia
      if (!navigator.mediaDevices?.getUserMedia) {
        warnings.push("Camera access not supported");
        this.browserSupported = false;
      }

      // Check face-api.js requirements (WebGL)
      const canvas = document.createElement("canvas");
      const gl =
        canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      if (!gl) {
        warnings.push("WebGL not supported - face detection may not work");
      }

      // Check if old browser
      const isOldBrowser =
        !window.Promise || !window.fetch || !window.IntersectionObserver;
      if (isOldBrowser) {
        warnings.push("Browser version is outdated");
        this.browserSupported = false;
      }

      this.browserWarnings = warnings;

      if (warnings.length > 0) {
        this.announce(
          `Browser compatibility issues detected: ${warnings.join(", ")}`,
        );
      }
    },

    detectMultipleFaces(detections) {
      // This should be called from face detection if we detect all faces
      // For now, we detect single face, but this would handle multiple
      this.faceCount = Array.isArray(detections)
        ? detections.length
        : detections
        ? 1
        : 0;
      this.multipleFacesDetected = this.faceCount > 1;

      if (this.multipleFacesDetected) {
        this.faceWarning = `${this.faceCount} faces detected - please ensure you're alone`;
        this.announce(`Warning: ${this.faceCount} faces detected in frame`);
        this.playErrorSound();
      }
    },

    lockOrientation() {
      // Lock to portrait on mobile
      if (screen.orientation && screen.orientation.lock) {
        screen.orientation
          .lock("portrait")
          .then(() => {
            this.orientationLocked = true;
            this.log("Orientation locked to portrait");
          })
          .catch((err) => {
            this.log("Could not lock orientation:", err);
          });
      }

      // Listen for orientation changes
      window.addEventListener("orientationchange", () => {
        if (window.orientation !== 0 && window.orientation !== 180) {
          this.announce("Please rotate your device to portrait mode");
          this.playErrorSound();
        }
      });
    },

    // ─── Progress Persistence ───────────────────────────────────────

    generateSessionId() {
      if (!this.sessionId) {
        this.sessionId = `biometric_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
      }
    },

    saveProgress() {
      if (!this.sessionId) return;

      const progress = {
        sessionId: this.sessionId,
        mode: this.internalMode,
        livenessMode: this.livenessMode,
        challenges: this.currentChallenges,
        completedChallenges: this.currentChallenges.filter((c) => c.completed)
          .length,
        timestamp: Date.now(),
        state: this.state,
      };

      try {
        localStorage.setItem(
          `biometric_progress_${this.sessionId}`,
          JSON.stringify(progress),
        );
        this.progressSaved = true;
        this.log("Progress saved");
      } catch (err) {
        console.warn("Could not save progress:", err);
      }
    },

    loadSavedProgress() {
      try {
        // Look for any saved progress in last 10 minutes
        const keys = Object.keys(localStorage).filter((k) =>
          k.startsWith("biometric_progress_"),
        );
        const now = Date.now();

        for (const key of keys) {
          const data = JSON.parse(localStorage.getItem(key));
          if (now - data.timestamp < 10 * 60 * 1000) {
            // 10 minutes
            // Found recent session
            if (confirm("Would you like to resume your previous session?")) {
              this.sessionId = data.sessionId;
              this.internalMode = data.mode;
              this.currentChallenges = data.challenges;
              this.resumedSession = true;
              this.announce("Session resumed");
              this.log("Progress restored");
              return;
            } else {
              localStorage.removeItem(key);
            }
          } else {
            // Clean up old sessions
            localStorage.removeItem(key);
          }
        }
      } catch (err) {
        console.warn("Could not load progress:", err);
      }
    },

    clearSavedProgress() {
      if (this.sessionId) {
        try {
          localStorage.removeItem(`biometric_progress_${this.sessionId}`);
        } catch (err) {
          console.warn("Could not clear progress:", err);
        }
      }
    },
  },
};
</script>

<style scoped>
.biometric-widget {
  width: 100%;
  height: 100%;
  min-height: 480px;
  background: #000;
  color: var(--text-color, #fff);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  border-radius: 20px;
}

.camera-screen {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.camera-feed {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
  background: #111;
}

.top-bar {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px 12px;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.75) 0%,
    transparent 100%
  );
}

.brand-logo img {
  max-height: 32px;
  object-fit: contain;
  margin-bottom: 12px;
}

.liveness-mode-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 20;
  letter-spacing: 0.3px;
}

.segmented-control {
  display: flex;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 3px;
  width: 100%;
  max-width: 280px;
}

.segmented-control button {
  flex: 1;
  padding: 10px 0;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.segmented-control button.active {
  background: rgba(255, 255, 255, 0.95);
  color: #111;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.face-guide-wrap {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -58%);
  width: 70%;
  max-width: 260px;
  aspect-ratio: 200 / 260;
  pointer-events: none;
  z-index: 5;
}

.face-guide-svg {
  width: 100%;
  height: 100%;
}

.positioning-instructions {
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  width: 92%;
  max-width: 420px;
  pointer-events: none;
}

.instruction-text {
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 16px 24px;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1);
  line-height: 1.5;
  letter-spacing: 0.3px;
  animation: instructionPulse 2s ease-in-out infinite;
}

@keyframes instructionPulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6),
      0 0 0 2px rgba(255, 255, 255, 0.2);
  }
}

.instruction-text.warning {
  background: rgba(255, 59, 48, 0.95);
  animation: warningPulse 1s ease-in-out infinite;
}

@keyframes warningPulse {
  0%,
  100% {
    box-shadow: 0 8px 32px rgba(255, 59, 48, 0.5),
      0 0 0 2px rgba(255, 255, 255, 0.2);
  }
  50% {
    box-shadow: 0 12px 40px rgba(255, 59, 48, 0.7),
      0 0 0 3px rgba(255, 255, 255, 0.4);
  }
}

.detection-badge {
  position: absolute;
  top: 145px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 999px;
  white-space: nowrap;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  letter-spacing: 0.2px;
}

.detection-badge.active {
  background: var(--success-color);
  box-shadow: 0 6px 20px rgba(52, 199, 89, 0.4);
  animation: badgePulse 1.5s ease-in-out infinite;
}

@keyframes badgePulse {
  0%,
  100% {
    transform: translateX(-50%) scale(1);
  }
  50% {
    transform: translateX(-50%) scale(1.05);
  }
}

.detection-badge.warning {
  background: var(--error-color);
  box-shadow: 0 6px 20px rgba(255, 59, 48, 0.4);
}

.record-prompt {
  position: absolute;
  bottom: 200px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 350px;
  z-index: 15;
  text-align: center;
}

.record-prompt span {
  display: block;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  font-size: 17px;
  font-weight: 600;
  padding: 16px 24px;
  border-radius: 16px;
  margin-bottom: 12px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  letter-spacing: 0.3px;
  line-height: 1.4;
}

.progress-bar {
  height: 5px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--success-color);
  transition: width 0.3s ease;
}

.bottom-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px 32px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
}

.mode-hint-container {
  width: 100%;
  max-width: 90%;
  margin: 0 auto 20px;
}

.mode-hint {
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  line-height: 1.5;
  padding: 14px 24px;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  letter-spacing: 0.3px;
}

.recording-progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
}

.recording-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #2563eb 0%, #3b82f6 100%);
  border-radius: 2px;
  transition: width 0.1s linear;
  box-shadow: 0 0 8px rgba(37, 99, 235, 0.6);
}

.btn-capture {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  max-width: 280px;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.95);
  color: #111;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-bottom: 14px;
}

.btn-capture:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-capture:hover:not(:disabled) {
  background: #fff;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
}

.shutter-ring {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.shutter-ring.record {
  border-color: var(--error-color);
}

.shutter-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--primary-color);
}

.shutter-ring.record .shutter-dot {
  background: var(--error-color);
}

.btn-label {
  flex: 1;
  text-align: center;
}

.recording-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 59, 48, 0.85);
  color: #fff;
  padding: 12px 24px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 14px;
}

.pulse {
  width: 10px;
  height: 10px;
  background: #fff;
  border-radius: 50%;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
}

.edge-highlights .pulse-arc {
  animation: pulseArc 0.8s ease-in-out infinite;
  opacity: 0.95;
  filter: drop-shadow(0 0 6px rgba(37, 99, 235, 0.6));
}

@keyframes pulseArc {
  0%,
  100% {
    opacity: 0.4;
    stroke-width: 6;
  }
  50% {
    opacity: 1;
    stroke-width: 10;
  }
}

@keyframes featureGuidePulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.95;
  }
}

.fallback-link-bottom {
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  text-decoration: none;
}
.fallback-link-bottom:hover {
  color: #fff;
  text-decoration: underline;
}

.fallback-link {
  color: var(--primary-color);
  font-size: 14px;
  text-decoration: none;
  margin-top: 10px;
}
.fallback-link:hover {
  text-decoration: underline;
}

.btn-x {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 20;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  border: none;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-x:hover {
  background: rgba(0, 0, 0, 0.7);
}

.overlay-message {
  position: absolute;
  inset: 0;
  z-index: 15;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  text-align: center;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.perm-icon {
  font-size: 56px;
  margin-bottom: 16px;
}

.perm-title {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 10px;
}

.perm-sub {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0 0 24px;
  max-width: 280px;
  line-height: 1.5;
}

.perm-steps {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
  text-align: left;
  max-width: 280px;
}
.perm-steps p {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  margin: 0 0 6px;
  line-height: 1.5;
}
.perm-steps p:last-child {
  margin-bottom: 0;
}

.btn-allow {
  width: 100%;
  max-width: 260px;
  padding: 14px 20px;
  background: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 12px;
  transition: opacity 0.2s;
}
.btn-allow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-allow:hover:not(:disabled) {
  opacity: 0.9;
}

.status-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px 24px;
  text-align: center;
  background: var(--bg-color, #000);
}

.status-icon {
  font-size: 64px;
  margin-bottom: 20px;
  line-height: 1;
}

.success-icon {
  color: var(--success-color);
}
.error-icon {
  color: var(--error-color);
}

.status-screen h2 {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 10px;
  color: grey;
}

.status-screen p {
  font-size: 15px;
  color: grey;
  margin: 0 0 24px;
  max-width: 280px;
  line-height: 1.5;
}

.spinner {
  width: 56px;
  height: 56px;
  border: 4px solid rgba(255, 255, 255, 0.15);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
  margin-bottom: 24px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn-primary {
  width: 100%;
  max-width: 280px;
  padding: 14px 20px;
  background: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 12px;
  transition: opacity 0.2s;
}
.btn-primary:hover {
  opacity: 0.9;
}

@media (max-width: 400px) {
  .face-guide-wrap {
    width: 78%;
  }
  .detection-badge {
    top: 95px;
    font-size: 12px;
    padding: 7px 16px;
  }
  .btn-capture {
    font-size: 15px;
    padding: 12px 16px;
  }
}

/* ═══════════════════════════════════════════════════════════════════
   ─── Accessibility & Audio Toggle ───
   ═══════════════════════════════════════════════════════════════════ */

/* Quality panel REMOVED - keeps UI clean and oval unobstructed */

/* ─── Accessibility ─── */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.sr-announcer {
  position: fixed;
  top: -9999px;
  left: -9999px;
}

/* ─── Audio Toggle Button ─── */
.audio-toggle {
  position: absolute;
  top: 80px;
  left: 16px;
  z-index: 20;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.audio-toggle:hover {
  background: rgba(0, 0, 0, 0.9);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.05);
}

.audio-toggle:active {
  transform: scale(0.95);
}

/* ─── High Contrast Mode ─── */
@media (prefers-contrast: high) {
  .biometric-widget {
    border: 3px solid white;
  }

  .face-guide-svg ellipse {
    stroke-width: 5 !important;
  }

  .btn-capture,
  .btn-allow,
  .btn-primary {
    border: 2px solid white;
  }
}

/* ─── Reduced Motion ─── */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  .pulse-arc {
    animation: none !important;
  }

  .instructionPulse,
  .warningPulse,
  .badgePulse,
  .slideDown {
    animation: none !important;
  }
}

/* ─── Focus Indicators (Keyboard Navigation) ─── */
button:focus-visible,
a:focus-visible {
  outline: 3px solid var(--primary-color);
  outline-offset: 2px;
}

.segmented-control button:focus-visible {
  outline: 2px solid white;
  outline-offset: -2px;
  border-radius: 8px;
}

/* ─── Mobile Orientation Warning ─── */
@media (orientation: landscape) and (max-height: 500px) {
  .biometric-widget::before {
    content: "⤴️ Please rotate to portrait mode";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999;
    background: rgba(255, 59, 48, 0.95);
    color: white;
    padding: 20px 30px;
    border-radius: 16px;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    backdrop-filter: blur(12px);
  }
}
</style>
